"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Stethoscope,
  Sparkles,
  Target,
  HeartPulse,
  Brain,
  Activity,
  ArrowRight,
  Crown,
  Mic,
  Layers,
  ShieldCheck,
  Compass,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { CardGridBlock } from "@/lib/blocks/card-grid-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "83.3", suffix: "%", label: "Campaign Success", sub: "15 victories / 18 engagements", accent: "red" },
  { metric: "0", suffix: "", label: "Security Incidents", sub: "15+ years clean record", accent: "emerald" },
  { metric: "18", suffix: "", label: "Countries", sub: "Three continents", accent: "purple" },
  { metric: "15", suffix: "+", label: "Years", sub: "Sovereign-scale operations", accent: "amber-gold" },
];

const PRINCIPLES = [
  { num: "01", title: "Diagnose Before Treating", body: "The first rule of medicine and the most violated rule of leadership. Most organizations act on symptoms. The disease progresses undetected. The method demands forensic diagnosis before any intervention." },
  { num: "02", title: "Triage Under Pressure", body: "When everything is urgent, nothing is solved well. The leaders who fail rarely lack the ability to act. They lack the ability to rank. Triage is the skill of sequencing under fear." },
  { num: "03", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry through the ninety-five percent. Judgment handles the five percent that do not. Brilliance without a system is unsustainable. A system without judgment cannot adapt." },
  { num: "04", title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Restore it, and the culture transforms." },
  { num: "05", title: "First, Do No Harm", body: "The oldest instruction in medicine, the most forgotten in leadership. Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill." },
  { num: "06", title: "Regulate the Room", body: "The leader&apos;s state becomes the team&apos;s state. A leader who is composed under pressure produces a team that is composed under pressure. The discipline of governing one&apos;s own state is the hardest leadership skill." },
];

const FRAMEWORKS = [
  { title: "Structural Diagnosis", body: "Is the problem in the architecture — the design of roles, responsibilities, reporting lines, and decision rights? When the same problems recur across leadership transitions, the diagnosis is structural." },
  { title: "Process Diagnosis", body: "Is the problem in the workflows — the sequences of steps that convert inputs into outputs? When the same bottlenecks recur regardless of the people involved, the diagnosis is process." },
  { title: "Talent Diagnosis", body: "Is the problem in the people — the skills, capabilities, behaviors, or motivation of the individuals? When performance varies dramatically across individuals in the same role, the diagnosis is talent." },
  { title: "Incentive Diagnosis", body: "Is the problem in the incentive architecture — the mechanisms that reward behavior and shape decision-making? When people are doing what the incentive rewards but outcomes are undesirable, the diagnosis is incentive." },
  { title: "Cultural Diagnosis", body: "Is the problem in the underlying norms, values, and assumptions? When the organization says one thing and does another, the diagnosis is cultural." },
];

const APPLICATION = [
  { title: "Intelligence Operations", body: "Every engagement begins with intelligence gathering. The ten-stage refinement pipeline processes over 500 million daily data points. Diagnostic precision ensures action grounded in understanding." },
  { title: "Operations Management", body: "The neural command interface provides real-time visibility that enables triage: which platform requires immediate attention, which engagement is approaching critical status, which threat demands containment." },
  { title: "Platform Architecture", body: "Each platform operates on defined protocols — documented sequences that produce verified outcomes. The feedback loop ensures every engagement makes the next one smarter." },
  { title: "Crisis Response", body: "Five hundred-plus automated playbooks. But the playbooks are the protocol. The judgment is knowing which playbook to deploy, when to deviate, and when the situation demands response that no playbook anticipated." },
];

export default function LeadershipAsDiagnosisPage() {
  return (
    <>
      <PageHero
        icon={Stethoscope}
        overline="Leadership as Diagnosis"
        overlineClassName="text-red"
        title={
          <>
            What Medicine Teaches About <span className="text-gradient-premium">Running Organizations.</span>
          </>
        }
        subtitle="Leadership is taught as charisma, vision, and the keynote. In practice, it is diagnosis, triage, protocol, judgment, outcome accountability, and the discipline of not making things worse under pressure. These are clinical skills. They are the only skills that scale. The leadership that sustains organizations under pressure is the same leadership that sustains patients under the knife."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Leadership as Diagnosis" }]}
        badge="The clinical method applied"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
              <Sparkles className="size-3.5 text-red" />
              <span className="text-overline text-red">The Core Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Leadership is clinical, not charismatic.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The world teaches leadership as charisma. The vision statement. The keynote. The motivational narrative. The ability to inspire. This premise is wrong. It is not slightly wrong. It is catastrophically wrong. The cost is measured in organizations that collapse when the charismatic leader departs, teams that function only when the inspirational figure is present.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The most important leadership skills are not the ability to inspire. They are the ability to diagnose, to triage, to follow protocol while preserving judgment, to own outcomes rather than effort, to refrain from making things worse under pressure, and to regulate one&apos;s own state so that others can function. These are clinical skills.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <HeartPulse className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six Clinical Principles</span>
          </div>
          <h2 className="text-display-md mb-4">The operating system forged in clinical practice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six principles. Each addresses a specific failure mode. Together, they constitute the doctrine that governs every engagement at sovereign scale.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Diagnostic Framework</span>
          </div>
          <h2 className="text-display-md mb-4">Differential diagnosis for organizations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Five diagnostic dimensions. Each addresses a distinct class of organizational failure. The framework ensures the leader identifies the actual cause before prescribing intervention.
          </p>
        </div>
        <div className="space-y-3">
          {FRAMEWORKS.map((f, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-caption font-bold text-white">{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{f.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Activity className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Clinical Method in Practice</span>
          </div>
          <h2 className="text-display-md mb-4">From delivery room to boardroom to war room.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The six clinical principles are not theoretical frameworks. They are operational disciplines applied across eighteen countries, nine platforms, sixty-plus services, and fifteen-plus years.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {APPLICATION.map((a, i) => {
            const accents = ["red", "purple", "cyan", "emerald"];
            const ac = ACCENT[accents[i]];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border}`}
              >
                <h3 className={`text-heading-md mb-3 ${ac.text}`}>{a.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{a.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Compass className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Why It Scales</span>
          </div>
          <h2 className="text-display-md mb-4">The leadership that sustains organizations under pressure.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Medicine is the only profession that has systematized excellence to the point of making it predictable. The parallel is not metaphorical. It is structural.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { icon: Target, title: "Diagnosis Drives Action", body: "Every intervention grounded in understanding. The 89% crisis prediction accuracy is not a technology achievement. It is a diagnostic achievement." },
            { icon: ShieldCheck, title: "Protocols That Scale", body: "Documented sequences that produce predictable outcomes. The system is the genius, not the individual. Outcomes do not depend on who operates it." },
            { icon: Compass, title: "Judgment That Adapts", body: "The mature operator loves the protocol and trusts it — and knows exactly when to put it down. Five percent of cases require judgment. The operator keeps it." },
          ].map((b, i) => {
            const accents = ["red", "purple", "cyan"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                  <b.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{b.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="diagnostic-tabs" ariaLabel="Diagnostic Framework Tabs">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Diagnostic Framework Tabs</span>
          </div>
          <h2 className="text-display-md mb-4">Five diagnostic lenses. One clinical method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Each lens represents a different way the leader learns to see. The clinical method is not a single skill. It is the disciplined application of multiple lenses to the same problem.
          </p>
        </div>
        <TabsBlock
          defaultValue="structural"
          tabs={[
            {
              value: "structural",
              label: "Structural",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-cyan">Structural Diagnosis</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    The architecture question. Is the problem in the design — the roles, responsibilities, reporting lines, decision rights that constitute the organization&apos;s structure? When the same problems recur across leadership transitions, the diagnosis is structural. The architecture determines what the system can produce regardless of who occupies it.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Structural diagnosis demands forensic attention to authority gradients, information flow, accountability chains, and decision rights. The structural question is asked before any other because no amount of process improvement or talent selection will fix a structure that is fundamentally misaligned.
                  </p>
                </div>
              ),
            },
            {
              value: "process",
              label: "Process",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-cyan">Process Diagnosis</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    The workflow question. Is the problem in the sequence of steps that convert inputs into outputs? When the same bottlenecks recur regardless of the people involved, the diagnosis is process. The process is the protocol. The protocol can be wrong, can be inefficient, can be misaligned with the structure.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Process diagnosis demands attention to handoffs, decision points, escalation procedures, and verification checkpoints. The most powerful process insight is recognizing that the existing process is solving the wrong problem — and the workflow was designed when the inputs and outputs were different from what they are today.
                  </p>
                </div>
              ),
            },
            {
              value: "talent",
              label: "Talent",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-cyan">Talent Diagnosis</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    The people question. Is the problem in the skills, capabilities, behaviors, or motivation of the individuals? When performance varies dramatically across individuals in the same role, the diagnosis is talent. The 70% Operating Model produces teams where the talent question is the least frequent diagnosis.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Talent diagnosis demands attention to selection criteria, training programs, performance management, and incentive structures. The talent question is asked third because talent is only as effective as the structure and process within which it operates.
                  </p>
                </div>
              ),
            },
            {
              value: "incentive",
              label: "Incentive",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-cyan">Incentive Diagnosis</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    The motivation question. Is the problem in the mechanisms that reward behavior and shape decision-making? When people are doing what the incentive rewards but outcomes are undesirable, the diagnosis is incentive. The incentive architecture is invisible to the people operating within it — they only see the rewards.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Incentive diagnosis demands attention to compensation, recognition, advancement, and the implicit signals sent by resource allocation. The incentive question is asked fourth because incentives are the most difficult to redesign — and the most consequential when misaligned.
                  </p>
                </div>
              ),
            },
            {
              value: "cultural",
              label: "Cultural",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-cyan">Cultural Diagnosis</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    The deepest question. Is the problem in the underlying norms, values, and assumptions? When the organization says one thing and does another, the diagnosis is cultural. The culture is the most powerful force in the organization — and the most difficult to change.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Cultural diagnosis demands attention to the rituals, the unspoken rules, the stories the organization tells about itself. The cultural question is asked last because cultural change requires structural, process, talent, and incentive alignment first. Without these, the cultural change is performance.
                  </p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six Frameworks in Practice</span>
          </div>
          <h2 className="text-display-md mb-4">Six clinical frameworks for the field commander.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The six frameworks that operationalize the six clinical principles. Each framework is a discipline that produces the diagnostic, the protocol, or the verification required to lead under sovereign-scale pressure.
          </p>
        </div>
        <PillarGridBlock
          columns={3}
          variant="card"
          pillars={[
            { title: "Differential Diagnosis", body: "The discipline of generating multiple hypotheses before testing any. The leader who commits to a single diagnosis before examining alternatives will miss the actual cause more often than not.", icon: <Stethoscope className="size-5" />, accent: "red" },
            { title: "Triage Framework", body: "The discipline of ranking under fear. The framework that ensures the critical is addressed first, the urgent second, the deferrable third — regardless of political pressure to invert the priority.", icon: <HeartPulse className="size-5" />, accent: "purple" },
            { title: "Protocol Framework", body: "The discipline of operationalized excellence. The framework that turns the protocol from aspiration into architecture — the protocol carries the ninety-five percent.", icon: <Target className="size-5" />, accent: "cyan" },
            { title: "Judgment Framework", body: "The discipline of knowing when to deviate. The framework that distinguishes the five percent of cases requiring judgment from the ninety-five percent requiring protocol adherence.", icon: <Brain className="size-5" />, accent: "emerald" },
            { title: "Outcome Framework", body: "The discipline of measuring what matters. The framework that restores outcome accountability — the organization is measured by what it delivered, not by how hard it tried.", icon: <Activity className="size-5" />, accent: "amber-gold" },
            { title: "Do No Harm Framework", body: "The discipline of not making things worse. The framework that requires the leader to ask, before any intervention: what is the downside if this is wrong? The answer determines the speed and scale of action.", icon: <ShieldCheck className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Activity className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Application Domains</span>
          </div>
          <h2 className="text-display-md mb-4">Where the clinical method is applied.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The clinical method is not a single profession. It is the discipline that scales across any domain where the cost of failure is meaningful and the requirement for outcome accountability is absolute.
          </p>
        </div>
        <CardGridBlock
          columns={3}
          items={[
            { title: "Sovereign Application", body: "Heads of state, governments, royal families, defense agencies. The clinical method applied at national scale. The cost of error is national. The methodology is the same.", icon: <Crown className="size-5" />, accent: "red" },
            { title: "Enterprise Application", body: "Fortune-scale corporations, multinational enterprises, institutional boards. The clinical method applied to organizational leadership. CEREBRAS P5 has been deployed at fifty-plus Fortune 500 companies.", icon: <Target className="size-5" />, accent: "purple" },
            { title: "Crisis Application", body: "Crisis response, reputation recovery, organizational resilience. The clinical method applied to the most stressful conditions an organization can face. PHOENIX-1 operates at three hundred eighty-four to one thousand four hundred sixteen times the speed of traditional approaches.", icon: <Activity className="size-5" />, accent: "cyan" },
            { title: "Political Application", body: "Campaign engineering, electoral intelligence, governance transformation. The clinical method applied to the most demanding political environment. Eighty-three-point-three percent campaign success rate.", icon: <Layers className="size-5" />, accent: "emerald" },
            { title: "Security Application", body: "Sovereign-grade protection, zero-trust architecture, post-quantum cryptography. The clinical method applied to security. Zero security incidents across fifteen-plus years.", icon: <ShieldCheck className="size-5" />, accent: "amber-gold" },
            { title: "Operational Application", body: "Operational excellence, sovereign operations architecture, six-stage methodology. The clinical method applied to the operational layer. Eighty-five to ninety-five percent crisis success rate.", icon: <Sparkles className="size-5" />, accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage the clinical method.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Keynotes, advisory, board service, and operational consulting. The clinical skills that scale are available to every organization that demands outcomes over inspiration.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book Dr. Jyoti Kush
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Stethoscope, accent: "red" },
          { title: "Operational Excellence", description: "The engineered system that produces verified outcomes. Protocols, checklists, reviews, accountability.", href: "/expertise/operational-excellence/", icon: Target, accent: "purple" },
          { title: "Medicine Meets Technology", description: "The physician&apos;s discipline for the digital age. Clinical rigor applied to sovereign systems.", href: "/expertise/medicine-meets-technology/", icon: HeartPulse, accent: "emerald" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
